import { computed, ref } from "vue";

import { useCartStore } from "@/data/stores/cart";
import { useCartPaymentStore } from "@/data/stores/cartPayment";

import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
import type { ICustomerArticleWithOptions } from "@/data/model/Customer";
import type { IService } from "@/data/model/Service";

import { areSelectedOptionGroupsValid } from "./optionGroupsValidator";

export function useAddToCart(
    item: ICustomerArticleWithOptions,
    service: IService | null | undefined,
    allowAddWhenUpdating: boolean = false
) {
    // define stores
    const cart = useCartStore();
    const cartPayment = useCartPaymentStore();

    // reactive
    const quantityToAdd = ref(1);
    const openPrice = ref<number | null>(null);
    const selectedOptions = ref<IArticleWithOptions[]>([]);

    // computed
    const articleInCartQuantity = computed(() => cart.getCartItemQuantity(item.articleId));
    const minQuantity = computed(() => item.minQuantity ?? 1);
    const maxQuantity = computed(() => {
        const maxTotalPerArticle = item.maxQuantity ?? Number.MAX_VALUE;
        const maxAllowedPerArticle = maxTotalPerArticle - articleInCartQuantity.value;

        const maxTotalUnitsPerOrder = service?.useCase?.maximumTotalUnitsPerOrder ?? Number.MAX_VALUE;
        const maxAllowedUnitsPerOrder = maxTotalUnitsPerOrder - cart.totalCartMainItems;

        const max = Math.min(maxAllowedPerArticle, maxAllowedUnitsPerOrder);
        return Math.max(0, max);
    });

    const isOpenPriceValid = computed(() => {
        if (!item.isOpenPriceEnabled) return true;

        return openPrice.value !== null && !isNaN(openPrice.value);
    });

    const isAddToCartAvailable = computed(() => {
        const areOptionGroupsValid = areSelectedOptionGroupsValid(item.options, selectedOptions.value);

        return (
            (allowAddWhenUpdating || !cart.isUpdating) &&
            !cartPayment.isProcessingPaymentDevicePayment &&
            maxQuantity.value > 0 &&
            isOpenPriceValid.value &&
            areOptionGroupsValid
        );
    });

    async function addItemToCart() {
        return cart.addArticleToCart(
            item.articleId,
            item.categoryId,
            quantityToAdd.value,
            selectedOptions.value,
            service!,
            item.offerId!,
            openPrice.value ?? undefined
        );
    }

    function fixCurrentQuantity() {
        let va = quantityToAdd.value;
        if (va < minQuantity.value) {
            va = minQuantity.value;
        }

        if (va > maxQuantity.value) {
            va = maxQuantity.value;
        }
        quantityToAdd.value = va;
    }

    return {
        quantityToAdd,
        openPrice,
        selectedOptions,
        minQuantity,
        maxQuantity,
        isOpenPriceValid,
        isAddToCartAvailable,
        addItemToCart,
        fixCurrentQuantity,
    };
}
